import { Avatar, Link, List, ListItem, Popover } from '@material-ui/core';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { staticRoutes } from '~/config/routes';
import { features } from '~/features';
import { signOut } from '~/features/auth/store/actions.thunks';
import { selectAuthorizedState } from '~/store/user/selectors';
import css from '~/common/header/styles.module.scss';
import { useAppRoute } from '~/utils/hooks/useAppRoute';
import { useThunkDispatch } from '~/utils/hooks/useThunkDispatch';
import { useRouteMatch } from 'react-router-dom';
import * as CreatorsListFeature from '~/features/creators-list';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { getUserProfileData } from '~/features/dashboard/store/selectors';
import classNames from 'classnames';

type LinkEntity = {
    title: string;
    handler: () => any;
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        popover: {
            pointerEvents: 'none',
        },
        popoverContent: {
            pointerEvents: 'auto',
            // padding: theme.spacing(2),
        },
        root: {
            width: '100%',
            maxWidth: 360,
            // padding: theme.spacing(2),
            backgroundColor: theme.palette.background.paper,
        },
    })
);

const RoutesList: React.FC<{ list: LinkEntity[] }> = ({ list }) => {
    const classes = useStyles();
    const isAuthorized = useSelector(selectAuthorizedState);
    const buildHandler = useCallback(
        (handler: () => any) => (e: React.SyntheticEvent) => {
            e.preventDefault();
            handler();
        },
        []
    );

    return isAuthorized ? (
        <List component="nav" className={classes.root} aria-label="contacts">
            {list.map(({ title, handler }) => (
                <ListItem button onClick={buildHandler(handler)}>
                    {title}
                </ListItem>
            ))}
        </List>
    ) : (
        <div className={classNames(css.routesList, 'routes-list-override')}>
            {list.map(({ title, handler }) => (
                <Link className={'link-container-override'} key={title} onClick={buildHandler(handler)}>
                    {title}
                </Link>
            ))}
        </div>
    );
};

export const AppNavList: React.FC = () => {
    const isAuthorized = useSelector(selectAuthorizedState);
    const route = useAppRoute();
    const dispatch = useThunkDispatch();

    const classes = useStyles();

    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const togglePopover = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.stopPropagation();
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };
    const open = Boolean(anchorEl);
    const userProfileResponse = useSelector(getUserProfileData);

    // TODO: tmp solution. can't remove that line because of build error.
    const isDashboard = useRouteMatch(`/${CreatorsListFeature.name}`) && false; // eslint-disable-line

    const routesList = useMemo(() => {
        return [
            ...(isAuthorized && isDashboard
                ? [
                      {
                          title: 'Export',
                          handler: () => {
                              route(features[CreatorsListFeature.name].EXPORT_PATH);
                          },
                      },
                  ]
                : []),
            ...staticRoutes.map(({ link, title }) => ({
                title,
                handler: () => {
                    link.includes('http') ? (window.location.href = link) : route(link);
                },
            })),
            ...(!isAuthorized
                ? [
                      {
                          title: 'Log In',
                          handler: () => route(features[CreatorsListFeature.name].PROFILE_PATH),
                      },
                  ]
                : []),
            ...(isAuthorized
                ? [
                      {
                          title: 'Profile',
                          handler: () => {
                              route(features[CreatorsListFeature.name].PROFILE_PATH);
                          },
                      },
                      {
                          title: 'Log Out',
                          handler: () => dispatch(signOut()),
                      },
                  ]
                : []),
        ];
    }, [dispatch, isAuthorized, isDashboard, route]);
    return isAuthorized ? (
        <div>
            <Avatar alt={userProfileResponse?.firstName} src={userProfileResponse?.image} onClick={togglePopover} />
            <Popover
                id="mouse-over-popover"
                className={classes.popover}
                classes={{
                    paper: classes.popoverContent,
                }}
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                onClose={togglePopover}
                disableRestoreFocus
                PaperProps={{ onMouseLeave: togglePopover }}
            >
                <RoutesList list={routesList} />
            </Popover>
        </div>
    ) : (
        <RoutesList list={routesList} />
    );
};
